import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ApiKeysManager from './components/ApiKeysManager';

const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="relative inline-block ml-1 group">
    {children}
    <div className="absolute bottom-full mb-2 w-60 p-2 bg-gray-700 text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 z-10">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-gray-700"></div>
    </div>
  </div>
);

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [credits, setCredits] = useState(100); // Mock credits

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word[0]).join('').toUpperCase();
  };


  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-netlify-teal border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const userName = user.user_metadata?.full_name || 'User';
  const userEmail = user.email || '';
  const joinedDate = new Date(user.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate(-1)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
                <p className="text-gray-600">Manage your profile and account preferences</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'profile'
                    ? 'border-netlify-teal text-netlify-teal'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'security'
                    ? 'border-netlify-teal text-netlify-teal'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Security
              </button>
              <button
                onClick={() => setActiveTab('apiKeys')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'apiKeys'
                    ? 'border-netlify-teal text-netlify-teal'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                API Keys
              </button>
              <button
                onClick={() => setActiveTab('billing')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'billing'
                    ? 'border-netlify-teal text-netlify-teal'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Billing
              </button>
            </nav>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="p-6 space-y-6">
              {/* Profile Header */}
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <div className="w-20 h-20 bg-netlify-teal rounded-full flex items-center justify-center">
                    {user.user_metadata?.avatar_url ? (
                      <img src={user.user_metadata.avatar_url} alt={userName} className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                      <span className="text-2xl font-bold text-white">{getInitials(userName)}</span>
                    )}
                  </div>
                  <button className="absolute -bottom-2 -right-2 w-8 h-8 bg-white rounded-full border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
                    </svg>
                  </button>
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900">{userName}</h2>
                  <p className="text-gray-600">{userEmail}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      Free Plan
                    </span>
                    <span className="text-sm text-gray-500">
                      Joined {joinedDate}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 py-6 border-y border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">5</div>
                  <div className="text-sm text-gray-600">Agents Created</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">247</div>
                  <div className="text-sm text-gray-600">Messages Sent</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">98%</div>
                  <div className="text-sm text-gray-600">Uptime</div>
                </div>
              </div>

            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="p-6 space-y-6">
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Security Settings</h3>
                <p className="text-gray-600 mb-4">
                  Security settings are managed through Google OAuth. 
                  <br />Update your security preferences in your Google account.
                </p>
                <a 
                  href="https://myaccount.google.com/security" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-netlify-teal text-white rounded-lg hover:bg-netlify-600 font-medium transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                  </svg>
                  Manage Google Security
                </a>
              </div>
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === 'apiKeys' && <ApiKeysManager />}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="p-6 space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-gray-800">Billing</h3>
                <p className="text-gray-500 mt-1">Manage your subscription and billing details.</p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">Your Credits</h4>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{credits}</p>
                    <p className="text-sm text-gray-500">Available for extra messages.</p>
                  </div>
                  <button 
                    onClick={() => setCredits(c => c + 50)} 
                    className="px-6 py-2 bg-netlify-teal text-white rounded-lg font-semibold hover:bg-netlify-600 transition-colors"
                  >
                    Top Up
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Free Plan */}
                <div className="border rounded-lg p-6 flex flex-col bg-white shadow-sm">
                  <h4 className="text-lg font-semibold text-gray-800">Free</h4>
                  <p className="mt-4 text-3xl font-bold text-gray-900">$0<span className="text-base font-medium text-gray-500">/mo</span></p>
                  <p className="mt-2 text-sm text-gray-500">For individuals and small teams getting started.</p>
                  <ul className="mt-6 space-y-4 text-sm text-gray-600 flex-grow">
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span><strong>Unlimited agents</strong>, perfect for all your projects.</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span>
                        <strong>100 messages/month</strong>
                        <Tooltip text="Messages over your monthly limit will use your credits. The rate per message varies by model.">
                          <svg className="w-4 h-4 text-gray-400 inline-block cursor-pointer" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                        </Tooltip>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span>Share each agent with <strong>one other person</strong>.</span>
                    </li>
                  </ul>
                  <button className="mt-8 w-full py-2 px-4 bg-gray-100 text-gray-800 rounded-md font-semibold text-sm cursor-default">Your Current Plan</button>
                </div>

                {/* Starter Plan */}
                <div className="border-2 border-netlify-teal rounded-lg p-6 flex flex-col bg-white shadow-lg relative">
                  <div className="absolute -top-3.5 left-1/2 transform -translate-x-1/2">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold text-white bg-netlify-teal">Most Popular</span>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-800">Starter</h4>
                  <p className="mt-4 text-3xl font-bold text-gray-900">€10<span className="text-base font-medium text-gray-500">/mo</span></p>
                  <p className="mt-2 text-sm text-gray-500">For growing teams who need more power.</p>
                  <ul className="mt-6 space-y-4 text-sm text-gray-600 flex-grow">
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span>Access to <strong>premium models</strong>.</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span>
                        <strong>1,000 messages/month</strong>
                        <Tooltip text="Messages over your monthly limit will use your credits. The rate per message varies by model.">
                          <svg className="w-4 h-4 text-gray-400 inline-block cursor-pointer" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                        </Tooltip>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span><strong>Share with everyone</strong>, making collaboration seamless.</span>
                    </li>
                  </ul>
                  <button className="mt-8 w-full py-2 px-4 bg-netlify-teal text-white rounded-md font-semibold text-sm hover:bg-netlify-600 transition-colors">Upgrade to Starter</button>
                </div>

                {/* Pro Plan */}
                <div className="border rounded-lg p-6 flex flex-col bg-white shadow-sm">
                  <h4 className="text-lg font-semibold text-gray-800">Pro</h4>
                  <p className="mt-4 text-3xl font-bold text-gray-900">€40<span className="text-base font-medium text-gray-500">/mo</span></p>
                  <p className="mt-2 text-sm text-gray-500">For businesses that require advanced features.</p>
                  <ul className="mt-6 space-y-4 text-sm text-gray-600 flex-grow">
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span>
                        <strong>10,000 messages/month</strong>
                        <Tooltip text="Messages over your monthly limit will use your credits. The rate per message varies by model.">
                          <svg className="w-4 h-4 text-gray-400 inline-block cursor-pointer" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                        </Tooltip>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-4 h-4 text-netlify-teal mr-3 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                      <span>Includes a <strong>staging environment</strong> and <strong>detailed logs</strong>.</span>
                    </li>
                  </ul>
                  <button className="mt-8 w-full py-2 px-4 bg-gray-800 text-white rounded-md font-semibold text-sm hover:bg-gray-900 transition-colors">Upgrade to Pro</button>
                </div>
              </div>

              <div className="text-center text-sm text-gray-500 pt-6 border-t border-gray-200">
                <p>Message usage beyond your plan's included amount will be billed against your credits. The rate per message varies by model.</p>
                <a href="#!" className="text-netlify-teal hover:underline font-medium">Learn more about credit pricing</a>.
              </div>
            </div>
          )}
        </div>

        {/* Sign Out Button */}
        <div className="mt-8 flex justify-center">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
